import ApiSmile from '@/api/api-smile';
import ActionArea from '@/component/action-area';
import {ActionButton} from '@/component/action-area/action-area';
import DataRow from '@/component/data-row';
import PageContainer from '@/component/page-container';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import {DatePicker, Form, InputNumber, Modal, Select} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment, {Moment} from 'moment';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import './recommend-sell.scss';

interface RecommendSellData {
    recommendSellTime?: Moment;
    profitRate?: number;
    recommendSellReason?: string;
}

interface RecommendSellable {
    code: string;
    name: string;
}

const RecommendSell = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [recommendSellable, setRecommendSellable] = useState<RecommendSellable[]>([]);

    useEffect(() => {
        form.setFieldsValue({
            recommendSellTime: moment(),
            recommendSellReason: '',
        });
    }, [form]);

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getRecommendSellable();
            if (response?.data?.code === 0) {
                setRecommendSellable(response.data.data || []);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        load();
    }, []);

    const onCancelClick = () => {
        navigate('/recommend', {replace: true});
    };

    const validateData = (payload: RecommendSellData, sell?: RecommendSellable) => {
        if (!(sell?.code && sell?.name)) {
            MessageUtil.error('请选择名称和代码');
            return false;
        }
        if (!payload.recommendSellTime) {
            MessageUtil.error('推荐卖出时间不能为空');
            return false;
        }

        return true;
    };

    const onSaveClick = () => {
        const value = form.getFieldsValue() || {};
        const payload: RecommendSellData = {
            recommendSellTime: value.recommendSellTime,
            profitRate: value.profitRate || 0,
            recommendSellReason: value.recommendSellReason || '',
        };
        const sell = recommendSellable.find((item) => item.code === (value.code || ''));

        if (!validateData(payload, sell)) {
            return;
        }

        const create = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.createRecommendSell(sell?.code || '', payload);
            if (response?.data?.code === 0) {
                navigate(`/recommend`, {replace: true});
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定推荐卖出吗？',
            content: (
                <div>
                    <div>{`代码：${sell?.code || ''}`}</div>
                    <div>{`名称：${sell?.name || ''}`}</div>
                    <div>{`卖出时间：${payload.recommendSellTime?.format('YYYY-MM-DD') || ''}`}</div>
                    <div>{`收益率：${payload.profitRate || 0}`}</div>
                    <div>{`卖出原因：${payload.recommendSellReason || ''}`}</div>
                </div>
            ),
            onOk: create,
        });
    };

    const getButtons = () => {
        const buttons: ActionButton[] = [];
        buttons.push({text: '取消', onClick: onCancelClick});
        buttons.push({text: '保存', onClick: onSaveClick, type: 'primary'});
        return buttons;
    };

    return (
        <PageContainer className="page-recommend-sell">
            <ActionArea buttons={getButtons()} />
            <Form form={form}>
                <DataRow title="名称代码">
                    <Form.Item name={'code'}>
                        <Select showSearch optionFilterProp="children">
                            {recommendSellable.map((item) => (
                                <Select.Option key={item.code} value={item.code}>
                                    {`${item.name}(${item.code})`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </DataRow>
                <DataRow title="卖出时间">
                    <Form.Item name={'recommendSellTime'}>
                        <DatePicker />
                    </Form.Item>
                </DataRow>
                <DataRow title="卖出收益率">
                    <Form.Item name={'profitRate'}>
                        <InputNumber className="data-desc price-input" stringMode step={'0.01'} addonAfter="%" />
                    </Form.Item>
                </DataRow>
                <DataRow title="卖出原因">
                    <Form.Item name={'recommendSellReason'}>
                        <TextArea rows={4} className="data-desc" />
                    </Form.Item>
                </DataRow>
            </Form>
        </PageContainer>
    );
};

export default RecommendSell;
